import { useEffect, useState } from "preact/hooks";
import { Fragment, type JSX } from "preact";
import { api, type Site, type Stats } from "../../api";
import { Avatar } from "../../components/Avatar";
import { DATE_LOCALE, useI18n, type Dict, type Lang } from "../../lib/i18n";
import { downloadCsv, fileStamp, type Cell } from "../../lib/csv";
import { dateTime, duration } from "../../lib/format";

/**
 * How the support desk is doing: volume, speed, satisfaction, and when people
 * write. Every chart here is a single series, so there is one colour — the
 * console's own — stepped from light to dark for magnitude, and every chart
 * has its numbers available as text (tooltips and a table), never colour alone.
 */

type Range = "today" | "7d" | "30d" | "custom";

/** The longest custom range the server accepts, in days. */
const MAX_CUSTOM_DAYS = 366;

/**
 * Whether two dates make a range worth asking the server for: both given, in
 * order, and not longer than it accepts. Checked here so a half-typed range
 * shows a sentence instead of a refused request.
 */
export function customRangeProblem(from: string, to: string): "incomplete" | "invalid" | null {
  if (!from || !to) return "incomplete";
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return "invalid";
  return (end - start) / 86_400_000 + 1 > MAX_CUSTOM_DAYS ? "invalid" : null;
}

/** Everything on the screen as spreadsheet rows: the totals, then each table under its own header. */
export function statsCsv(stats: Stats, t: Dict, lang: Lang): Cell[][] {
  const s = t.stats;
  return [
    [s.csvMetric, s.csvValue],
    [s.conversations, stats.totals.conversations],
    [s.unanswered, stats.totals.unanswered],
    [s.firstResponse, duration(stats.totals.firstResponseAvg, lang)],
    [s.resolution, duration(stats.totals.resolutionAvg, lang)],
    [s.rating, stats.totals.ratingAvg],
    [s.distribution, stats.ratingDistribution.map((n, i) => `${i + 1}★ ${n}`).join("  ")],
    [],
    [s.date, s.count],
    ...stats.perDay.map((d) => [d.date, d.count]),
    [],
    [s.operator, s.handled, s.avgFirst, s.avgRating],
    ...stats.operators.map((o) => [o.displayName, o.handled, duration(o.firstResponseAvg, lang), o.ratingAvg]),
    [],
    [s.topics, s.count],
    ...stats.topics.map((topic) => [topic.tag, topic.count]),
  ];
}

/** A step of the one-hue ramp: 0 is the surface, 1 is full strength. */
function shade(fraction: number): string {
  const pct = Math.round(12 + fraction * 88);
  return `color-mix(in srgb, var(--sea) ${pct}%, var(--surface))`;
}

function shortDate(iso: string, lang: Lang): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString(DATE_LOCALE[lang], { day: "numeric", month: "short" });
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }): JSX.Element {
  return (
    <div class="tile">
      <span class="tile-label">{label}</span>
      <span class="tile-value">{value}</span>
      {sub ? <span class="tile-sub">{sub}</span> : null}
    </div>
  );
}

/** Which day labels fit under the bars: evenly spaced, and never the last two crowding each other. */
export function tickIndexes(count: number): number[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i);
  const every = count > 14 ? 7 : 2;
  const ticks: number[] = [];
  for (let i = 0; i < count; i += every) ticks.push(i);
  const last = count - 1;
  if (last - ticks[ticks.length - 1]! >= every / 2) ticks.push(last);
  else ticks[ticks.length - 1] = last;
  return ticks;
}

/**
 * Bars drawn with HTML rather than a scaled SVG, so the labels stay at their
 * real size however wide the card is. The hit area is the whole column.
 */
function PerDayChart({ data, lang, labels }: { data: Stats["perDay"]; lang: Lang; labels: { date: string; count: string; table: string } }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const max = Math.max(1, ...data.map((d) => d.count));
  const peak = data.findIndex((d) => d.count === max && d.count > 0);
  const ticks = new Set(tickIndexes(data.length));

  return (
    <div>
      <button class="btn ghost sm table-toggle" aria-pressed={asTable} onClick={() => setAsTable(!asTable)}>
        {labels.table}
      </button>
      {asTable ? (
        <table class="data-table">
          <thead>
            <tr>
              <th>{labels.date}</th>
              <th class="num">{labels.count}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.date}>
                <td>{shortDate(d.date, lang)}</td>
                <td class="num">{d.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div class="bars" role="img" aria-label={labels.count} onMouseLeave={() => setHover(null)}>
          {data.map((d, i) => {
            const showValue = hover === i || (hover === null && i === peak);
            return (
              <div
                key={d.date}
                class={`bar-col${hover === i ? " on" : ""}`}
                title={`${shortDate(d.date, lang)}: ${d.count}`}
                onMouseEnter={() => setHover(i)}
              >
                <div class="bar-area">
                  {showValue ? <span class="bar-value">{d.count}</span> : null}
                  {d.count > 0 ? <span class="bar" style={{ height: `${(d.count / max) * 100}%` }} /> : null}
                </div>
                <span class="bar-tick">{ticks.has(i) ? shortDate(d.date, lang) : ""}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Heatmap({ grid, weekdays, hint }: { grid: number[][]; weekdays: string[]; hint: string }): JSX.Element {
  const max = Math.max(1, ...grid.flat());
  return (
    <div class="heat-wrap">
      <div class="heat" role="table" aria-label={hint}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} class="heat-hour" aria-hidden="true">
            {h % 6 === 0 ? String(h).padStart(2, "0") : ""}
          </span>
        ))}
        {grid.map((row, d) => (
          <Fragment key={d}>
            <span class="heat-day">
              {weekdays[d]}
            </span>
            {row.map((n, h) => (
              <span
                key={`${d}-${h}`}
                class="heat-cell"
                style={{ background: n === 0 ? "var(--raised)" : shade(n / max) }}
                title={`${weekdays[d]} ${String(h).padStart(2, "0")}:00 – ${n}`}
              />
            ))}
          </Fragment>
        ))}
      </div>
      <p class="hint">{hint}</p>
    </div>
  );
}

export function StatsView(): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.stats;
  const [range, setRange] = useState<Range>("7d");
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState<number | undefined>(undefined);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .sites()
      .then((r) => setSites(r.sites))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    // A custom range waits until both ends are in; nothing is asked for a half-typed one.
    const problem = range === "custom" ? customRangeProblem(from, to) : null;
    if (problem === "incomplete") return;
    if (problem === "invalid") {
      setError(t.customInvalid);
      return;
    }
    setError(null);
    void api
      .stats(range === "custom" ? { from, to } : range, siteId)
      .then(setStats)
      .catch(() => setError(all.common.unexpected));
  }, [range, siteId, from, to, all, t.customInvalid]);

  const ranges: Array<[Range, string]> = [
    ["today", t.today],
    ["7d", t.week],
    ["30d", t.month],
    ["custom", t.custom],
  ];
  const s = stats;
  const maxTopic = Math.max(1, ...(s?.topics.map((x) => x.count) ?? [1]));
  const maxRating = Math.max(1, ...(s?.ratingDistribution ?? [1]));

  return (
    <div class="stats-page">
      <header class="stats-head">
        <h1>{t.title}</h1>
        <div class="stats-filters">
          <div class="segmented" role="radiogroup" aria-label={t.range}>
            {ranges.map(([value, label]) => (
              <button key={value} role="radio" aria-checked={range === value} onClick={() => setRange(value)}>
                {label}
              </button>
            ))}
          </div>
          {range === "custom" ? (
            <>
              <label class="range-date">
                {t.from}
                <input type="date" value={from} max={to || undefined} onInput={(e) => setFrom(e.currentTarget.value)} />
              </label>
              <label class="range-date">
                {t.to}
                <input type="date" value={to} min={from || undefined} onInput={(e) => setTo(e.currentTarget.value)} />
              </label>
            </>
          ) : null}
          {sites.length > 1 ? (
            <select aria-label={all.common.site} value={siteId ?? ""} onChange={(e) => setSiteId(e.currentTarget.value ? Number(e.currentTarget.value) : undefined)}>
              <option value="">{all.canned.allSites}</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          ) : null}
          <button
            class="btn"
            disabled={!stats}
            onClick={() => {
              if (stats) downloadCsv(`statistics-${fileStamp()}.csv`, statsCsv(stats, all, lang));
            }}
          >
            {all.common.exportCsv}
          </button>
        </div>
      </header>

      {error ? <div class="alert error">{error}</div> : null}
      {!s ? (
        <div class="empty">{all.common.loading}</div>
      ) : (
        <>
          <section class="tiles">
            <Tile label={t.conversations} value={String(s.totals.conversations)} />
            <Tile label={t.unanswered} value={String(s.totals.unanswered)} />
            <Tile
              label={t.firstResponse}
              value={duration(s.totals.firstResponseAvg, lang)}
              sub={s.totals.firstResponseMedian !== null ? t.firstResponseMedian(duration(s.totals.firstResponseMedian, lang)) : undefined}
            />
            <Tile label={t.resolution} value={duration(s.totals.resolutionAvg, lang)} />
            <Tile
              label={t.rating}
              value={s.totals.ratingAvg !== null ? `${s.totals.ratingAvg.toLocaleString(DATE_LOCALE[lang])} / 5` : t.none}
              sub={t.ratingCount(s.totals.ratingCount)}
            />
          </section>

          {s.totals.conversations === 0 ? <div class="alert">{t.noData}</div> : null}

          <div class="stats-grid">
            <section class="card wide">
              <h3>{t.perDay}</h3>
              <PerDayChart data={s.perDay} lang={lang} labels={{ date: t.date, count: t.count, table: t.tableView }} />
            </section>

            <section class="card wide">
              <h3>{t.busiest}</h3>
              <Heatmap grid={s.heatmap} weekdays={t.weekdays} hint={t.busiestHint} />
            </section>

            <section class="card">
              <h3>{t.distribution}</h3>
              <ul class="hbars">
                {[5, 4, 3, 2, 1].map((stars) => {
                  const n = s.ratingDistribution[stars - 1] ?? 0;
                  return (
                    <li key={stars}>
                      <span class="hbar-label">{"★".repeat(stars)}</span>
                      <span class="hbar-track">
                        {n > 0 ? <span class="hbar" style={{ width: `${(n / maxRating) * 100}%`, background: shade(0.75) }} /> : null}
                      </span>
                      <span class="hbar-n num">{n}</span>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section class="card">
              <h3>{t.topics}</h3>
              <p class="hint" style={{ marginTop: "-4px" }}>
                {t.topicsHint}
              </p>
              {s.topics.length === 0 ? (
                <p class="hint">{t.noData}</p>
              ) : (
                <ul class="hbars">
                  {s.topics.map((topic) => (
                    <li key={topic.tag}>
                      <span class="hbar-label">{topic.tag}</span>
                      <span class="hbar-track">
                        {topic.count > 0 ? <span class="hbar" style={{ width: `${(topic.count / maxTopic) * 100}%`, background: shade(0.75) }} /> : null}
                      </span>
                      <span class="hbar-n num">{topic.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section class="card wide">
              <h3>{t.team}</h3>
              {s.operators.length === 0 ? (
                <p class="hint">{t.noData}</p>
              ) : (
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>{t.operator}</th>
                      <th class="num">{t.handled}</th>
                      <th class="num">{t.avgFirst}</th>
                      <th class="num">{t.avgRating}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.operators.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <span class="who-cell">
                            <Avatar name={o.displayName} color={o.color} url={o.avatarUrl} size={24} />
                            {o.displayName}
                          </span>
                        </td>
                        <td class="num">{o.handled}</td>
                        <td class="num">{duration(o.firstResponseAvg, lang)}</td>
                        <td class="num">{o.ratingAvg !== null ? o.ratingAvg.toLocaleString(DATE_LOCALE[lang]) : t.none}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section class="card wide">
              <h3>{t.comments}</h3>
              {s.comments.length === 0 ? (
                <p class="hint">{t.noData}</p>
              ) : (
                <ul class="comments">
                  {s.comments.map((c, i) => (
                    <li key={i}>
                      <span class="stars" aria-label={all.context.stars(c.rating)}>
                        {"★".repeat(c.rating)}
                        <span style={{ opacity: 0.3 }}>{"★".repeat(5 - c.rating)}</span>
                      </span>
                      <q>{c.comment}</q>
                      <span class="hint">{dateTime(c.createdAt, lang)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
